<!--
    ONE person of the delivery: a header band carrying the role as an eyebrow and the name, then the
    person's own figures and one expander for the rest.
-->
@if (person) {

    <!--
        overflow-hidden lets the header band run edge to edge inside the rounded border — the band
        carries no radius of its own and the card clips its top corners. Same construction as the
        Deckung and the Objekt card, so the three read as one family of cards.
    -->
    <div class="surface-card border-1 surface-border border-round overflow-hidden">

        <!--
            The header BAND. `surface-100` — the sheet's own subtle section background, never a new
            colour — plus the card's one divider, so the identity of the person sits visibly apart
            from the figures below it.

            No accent spine, unlike the Deckung card: persons are not grouped into families on this
            sheet, and a coloured bar would state a grouping that does not exist.
        -->
        <div class="surface-100 border-bottom-1 surface-border" [ngClass]="headClass">

            <!--
                The role above the name, not behind it: it is what a reader scans a Personen list for,
                and behind an ellipsised name it would be the first thing cut off.

                It is matched together with the name (`countTextHits(name, role)` publishes one total),
                so it takes the same mark as the name below: the screen states "the term is in this
                card's own text" and never guesses which of the two carried it. The mark is on an INNER
                span, because this element is a full-width block and washing it would paint a bar
                across the band instead of marking three words.

                The muted tone stays on the DIV and the mark carries its own colour on the SPAN: the
                utility is !important but it sits on the parent, and an inherited value always loses to
                the element's own declaration. Which matters — a marked role that kept the muted tone
                measures 3.58:1 on the hit wash where the body colour gives 7.78:1.
            -->
            @if (person.role) {
                <div class="cm-ed-eyebrow text-xs uppercase text-color-secondary"><span [class.cm-ed-hit-title]="ownTextHit">{{person.role}}</span></div>
            }

            <!--
                The name ellipsises rather than wraps, so every card in a row keeps the same first line
                and its figures stay aligned with its neighbours'. title keeps the full name reachable.

                `text-color`, not the emphasis colour: a grid of a dozen cards each shouting its own
                name in the accent colour is the highlighter effect this sheet's palette avoids. The
                sheet spends `text-primary` on section headings and on actions.

                THE NAME IS THE MOST SEARCHED STRING ON THIS SHEET, and it is what a search used to
                leave untouched: "Anna Meier" raised the "{n} Treffer" beside it and changed nothing
                else, so the reader saw THAT the card matched and never WHERE. `.cm-ed-hit-title` is
                the sheet's one treatment for "the term is in this heading" — the same class the
                Deckung card, the Objekt card, the section heading and every row heading wear.

                On an INNER span, because this element is a flex item with `flex-1` and owns the free
                width of the row: washing it would paint a bar from the name to the hit marker instead
                of marking the name. The ellipsis is unaffected — `text-overflow` works on the outer
                block's overflowing line box however its inline content is nested.

                A flex ROW rather than a bare div, so the hit marker can sit at the end of the line the
                reader is already looking at: this card's header is STACKED (role above name) and has
                no right-hand slot of its own, unlike the Deckung and Objekt bands. With one child and
                no marker it lays out exactly as the bare div did, and `min-w-0 + flex-1` is what keeps
                the ellipsis biting inside the row.
            -->
            <div class="flex align-items-baseline gap-2">

                <div class="font-bold text-color flex-1 min-w-0 overflow-hidden text-overflow-ellipsis white-space-nowrap"
                     [ngClass]="nameClass"
                     [title]="person.name"><span [class.cm-ed-hit-title]="ownTextHit">{{person.name}}</span></div>

                <!--
                    WHAT THIS CARD HOLDS FOR THE CURRENT TERM. Nothing on this sheet dims or
                    disappears during a search, so a person card with matches inside its collapsed
                    level-2 block looks exactly like one with none — this marker is the only thing on
                    it that says otherwise, and it is what lets a reader scan a Personen grid.

                    Only while a term is active, and only when there is something to declare: a
                    "0 Treffer" on every card would bury the card that has hits.
                -->
                @if (searching && person.hitCount) {
                    <span class="cm-ed-hits bg-primary-50 text-primary border-1 border-primary border-round-sm white-space-nowrap">{{person.hitCount}} Treffer</span>
                }

            </div>

            <!--
                There is no second line under the name, and no member on the view model for one: the
                Personen block delivers a person's name and role and nothing else that belongs above the
                pairs. Everything else the person carries is a FIELD and renders as a pair below.
            -->

        </div>

        <!-- The card's body. -->
        <div [ngClass]="padClass">

            <!--
                BASISDATEN — ONE renderer for every pair on this screen (see mvs-cm-ed-kv-list), and
                the level is stated rather than left to the default: this card holds two lists of the
                same shape, and they are exactly where the two levels have to be told apart.
            -->
            @if (person.level1.length) {
                <mvs-cm-ed-kv-list [fields]="person.level1"
                                   [size]="size"
                                   emphasis="basis"
                                   [searching]="searching"></mvs-cm-ed-kv-list>
            }

            <!--
                Rendered ONLY when there is something behind it — never a dead "0 weitere Angaben". A
                p-button, so Space and Enter activate it and the focus ring is the theme's; the
                disclosure ARIA travels through [pt].root onto the element that carries the button role.

                No `severity="secondary"`: it is an ACTION and wears the theme's primary colour, like
                the same control on the Deckung and Objekt cards. As a grey text button it read as a
                caption and was routinely missed.

                The top margin is the PrimeFlex utility and not a SCSS declaration: p-button forwards its
                styleClass onto that very element, so a utility that exists must not be re-declared.

                The label follows `open`, not `expanded`: while a term is active the block may already
                be on screen because the search put it there, and "3 weitere Angaben →" over visible
                pairs is a control that lies about what it does.
            -->
            @if (person.level2.length) {
                <p-button [label]="open ? 'Weniger anzeigen' : person.level2.length + ' weitere Angaben →'"
                          size="small"
                          [text]="true"
                          styleClass="cm-ed-more mt-1"
                          [pt]="foldPt"
                          (onClick)="toggle()"></p-button>
            }

            <!--
                Always in the document, so aria-controls never points at a missing element.

                WEITERE ANGABEN, and it says so: the run label names the level and the emphasis step
                (lighter weight, same tone) shows it, so what the expander reveals is visibly not more
                basics. The label carries its own rule, which is why the old `pt-2 border-top-1` is
                gone — a divider and a labelled rule 4 px apart is two boundaries for one boundary.

                AUTO-REVEAL. `open` is the reader's own state OR, while a term is active, the search
                having found one of its matches inside these very pairs. A match behind a closed
                expander is a match the reader is never shown — and with nothing dimmed, an unopened
                card would look exactly like a card with nothing in it. Temporary, never written back.
            -->
            <div [attr.id]="level2Id">
                @if (open) {
                    <div class="mt-1">
                        <mvs-cm-ed-kv-list [fields]="person.level2"
                                           [size]="size"
                                           emphasis="weitere"
                                           runLabel="Weitere Angaben"
                                           [searching]="searching"></mvs-cm-ed-kv-list>
                    </div>
                }
            </div>

        </div>

    </div>
}
